import { AbstractMesh, Angle, Matrix, Vector3 } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs';
import { Zed } from '@modules/core';

import { clearArchetype, Entity } from '../../ecs';
import { RenderManager } from '../../manager';

import type { BaseSystem, SystemOperationDefinition } from '@modules/core';
import type { World } from 'miniplex';

/**
 * 설명 추가 필요
 *
 * @group System
 * @category System
 */
export class TransformSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('TransformSystem');

  private transforms;
  private transformsDirty;

  public dynamicTransforms;
  private staticTransforms;

  private staticTransformsDirty;

  public readonly activeDynamicTransforms;

  private lastTransforms = new Map<Entity, Matrix>();

  private hasNewMesh = false;

  private scene;
  private world;
  private readonly renderManager;

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [RenderManager.INJECTABLE_TOKEN]: renderManager,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: Pick<BabylonjsModule, 'scene'>;
    [RenderManager.INJECTABLE_TOKEN]: RenderManager;
  }) {
    this.world = world;
    this.scene = babylonjs.scene;
    this.renderManager = renderManager;

    this.transforms = world.with('TransformNode');
    this.transformsDirty = this.transforms.with('TransformDirty');

    this.dynamicTransforms = this.transforms.with('TransformMovable');
    this.staticTransforms = this.transforms.without('TransformMovable');

    this.staticTransformsDirty = this.transformsDirty.without('TransformMovable');
    this.activeDynamicTransforms = this.dynamicTransforms.with('Active');

    this.scene.onNewMeshAddedObservable.add(() => (this.hasNewMesh = true));
    this.transforms.onEntityAdded.subscribe((e) => this.whenAnyTransform(e));
    this.dynamicTransforms.onEntityAdded.subscribe((e) => this.whenDynamicTransform(e));
    this.staticTransforms.onEntityAdded.subscribe((e) => this.whenStaticTransform(e));
    this.transforms.onEntityAdded.subscribe(() => this.renderManager.renderUpdate());
    this.transforms.onEntityRemoved.subscribe(() => this.renderManager.renderUpdate());
  }
  dispose() {
    clearArchetype(this.transforms);
    clearArchetype(this.transformsDirty);
    clearArchetype(this.dynamicTransforms);
    clearArchetype(this.staticTransforms);
    clearArchetype(this.staticTransformsDirty);
    clearArchetype(this.activeDynamicTransforms);
    this.lastTransforms.clear();
  }

  private whenAnyTransform = (entity: (typeof this.transforms.entities)[number]) => {
    entity.TransformNode.metadata = {
      ...entity.TransformNode.metadata,
      ...{
        entityId: entity.entityId,
      },
    };
  };

  private whenDynamicTransform = (entity: (typeof this.dynamicTransforms.entities)[number]) => {
    const { TransformNode } = entity;
    TransformNode.unfreezeWorldMatrix();

    this.lastTransforms.set(entity, TransformNode.getWorldMatrix().clone());

    if (TransformNode instanceof AbstractMesh) {
      const scene = TransformNode.getScene();
      scene.selectionOctree?.dynamicContent.push(TransformNode);
    }
  };

  private whenStaticTransform = (entity: (typeof this.staticTransforms.entities)[number]) => {
    const { TransformNode } = entity;
    TransformNode.freezeWorldMatrix();

    this.lastTransforms.delete(entity);

    if (TransformNode instanceof AbstractMesh) {
      const scene = TransformNode.getScene();
      const index = scene.selectionOctree?.dynamicContent.indexOf(TransformNode);
      if (index >= 0) {
        scene.selectionOctree?.dynamicContent.splice(index, 1);
      }
    }
  };

  public beforeRenderExecute = () => {
    for (const entity of this.dynamicTransforms) {
      const lastTransform = this.lastTransforms.get(entity);
      if (!lastTransform) continue;

      const { TransformNode } = entity;

      const currentMatrix = TransformNode.getWorldMatrix();
      if (!currentMatrix.equals(lastTransform)) {
        this.world.addComponent(entity, 'TransformDirty', true);
      }
    }
  };

  public beforeRenderExecuteLast = () => {
    let shouldRender = this.hasNewMesh || this.transformsDirty.size > 0;
    if (shouldRender) this.renderManager.renderUpdate();

    if (this.scene.selectionOctree && (this.hasNewMesh || this.staticTransformsDirty.size > 0)) {
      this.scene.createOrUpdateSelectionOctree();
    }
    this.hasNewMesh = false;
  };

  public afterRenderExecute = () => {
    for (const entity of this.transformsDirty) {
      this.world.removeComponent(entity, 'TransformDirty');
      const lastTransform = this.lastTransforms.get(entity);
      if (!lastTransform) continue;

      const { TransformNode } = entity;

      lastTransform.copyFrom(TransformNode.getWorldMatrix());
    }
  };

  public updateTransform(entityId: string, position?: Vector3, rotationY?: number, ignoreYPosition?: boolean) {
    const isDirty = position !== undefined || rotationY !== undefined;
    if (isDirty) {
      const entity = this.activeDynamicTransforms.entities.find((e) => e.entityId == entityId);
      if (entity) {
        this.updatePosition(entity, position, rotationY, ignoreYPosition);
        this.renderManager.renderUpdate();
      }
    }
  }
  private updatePosition = (
    entity: (typeof this.activeDynamicTransforms.entities)[number],
    position?: Vector3,
    rotationY?: number,
    ignoreYPosition?: boolean
  ) => {
    const transformNode = entity.TransformNode;
    if (position !== undefined) {
      if (ignoreYPosition) position = new Vector3(position.x, transformNode.absolutePosition.y, position.z);
      transformNode.setAbsolutePosition(position);
    }

    if (rotationY !== undefined) {
      const y = Angle.FromDegrees(rotationY % 360).radians();
      transformNode.rotation = new Vector3(0, y, 0);
    }
  };

  public getMovableTransformEntities() {
    return this.activeDynamicTransforms.entities ?? [];
  }

  readonly operations: SystemOperationDefinition[] = [
    { group: 'beforeRender', before: ['*'], run: this.beforeRenderExecute },
    { group: 'beforeRender', after: ['*'], run: this.beforeRenderExecuteLast },
    { group: 'afterRender', run: this.afterRenderExecute },
  ];
}
